/**
 * Display formatting — one place (Batch 0 of the polish pass).
 *
 * ---------------------------------------------------------------------------
 * FORMATTING IS FOR READING. IT NEVER TOUCHES WHAT IS STORED OR SEARCHED.
 * ---------------------------------------------------------------------------
 * Everything here takes whatever is in the database and returns something to put
 * on a screen. Nothing calls it before a write, and lib/leadSearch.ts keeps
 * matching on raw digits — a search that only found numbers typed the way this
 * file prints them would be a worse search than the one we have.
 *
 * Money and date formatters join this file in Batch 2. It starts with phone
 * because phone had NOTHING: every number rendered exactly as it was typed or as
 * a provider sent it, and lead intake makes four formats certain on one screen.
 */

/**
 * "(416) 555-0134" for a North American number, and the input untouched for
 * anything else.
 *
 * TEN DIGITS, or eleven beginning with 1 — the country code is dropped rather
 * than shown, because every number in this business is domestic and "+1" on all
 * of them is noise that makes the one genuinely foreign number harder to spot.
 *
 * ANYTHING ELSE IS SHOWN AS ENTERED, deliberately. An extension, a UK number, a
 * note somebody typed into the phone field — reformatting those means guessing,
 * and a guess that drops a digit from a client's only contact number is a far
 * worse outcome than an inconsistent-looking row. The rule is: make the common
 * case uniform, and never destroy anything.
 */
export function formatPhone(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';

  const digits = s.replace(/\D/g, '');
  const ten =
    digits.length === 10 ? digits
    : digits.length === 11 && digits.startsWith('1') ? digits.slice(1)
    : null;
  if (!ten) return s;

  // An 11-digit number that is really ten plus a country code still has to be
  // ten digits of NANP: area codes and exchanges never start with 0 or 1.
  if (/^[01]/.test(ten)) return s;

  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

/**
 * The href for a tel: link — digits only, E.164 where we can be sure.
 *
 * Separate from the display form on purpose. Several call sites built this by
 * stripping spaces off whatever was on screen, which produces `tel:(416)
 * 555-0134` — dialable on most phones, and a silent failure on some.
 */
export function telHref(raw: unknown): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (digits.length === 10) return `tel:+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `tel:+${digits}`;
  // Typed with its own country code (+44 …): keep the +, so it stays E.164.
  if (String(raw ?? '').trim().startsWith('+') && digits) return `tel:+${digits}`;
  return `tel:${digits || String(raw ?? '').trim()}`;
}

/** A dash rather than a blank, for a table cell that must not look broken. */
export const phoneOrDash = (raw: unknown): string => formatPhone(raw) || '—';
